import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SideBar from '../Components/SideBar';
import Header from '../Components/Header';
import { 
    Button, 
    Paper, 
    Typography, 
    Box, 
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete
} from '@mui/material';
import { Search, Add, Edit, Delete, Visibility, FileDownload } from '@mui/icons-material';
import axios from 'axios';
import BASEURL from '../Connection/BASEURL';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import dayjs from 'dayjs';

const RenewPaymentList = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [payments, setPayments] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [deceasedData, setDeceasedData] = useState(null);
    const [deceasedId, setDeceasedId] = useState(null);
    const [maintenanceFeeAmount, setMaintenanceFeeAmount] = useState(0);
    const [allDeceased, setAllDeceased] = useState([]);
    const [allClients, setAllClients] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [formData, setFormData] = useState({
        customer_name: '',
        renewal_amount: '',
        balance: '',
        deceasedid: deceasedId
    });
    const [editFormData, setEditFormData] = useState({
        customer_name: '',
        renewal_amount: '',
        balance: '',
        deceasedid: deceasedId
    });
    const [submitting, setSubmitting] = useState(false);
    const [editing, setEditing] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (location.state?.deceasedId && location.state?.deceasedData) {
            setDeceasedId(location.state.deceasedId);
            setDeceasedData(location.state.deceasedData);
            setMaintenanceFeeAmount(location.state.maintenanceFeeAmount || 0);
            setFormData(prev => ({ 
                ...prev, 
                deceasedid: location.state.deceasedId,
                renewal_amount: location.state.maintenanceFeeAmount || '',
                balance: '0'
            }));
            fetchPayments();
            
            // Fetch fee data and calculate maintenance fee if sale_of_grave is available
            if (location.state.deceasedData?.sale_of_grave) {
                fetchFeeData(location.state.deceasedData.sale_of_grave);
            }
            
            // Auto-open modal if openModal flag is set
            if (location.state?.openModal) {
                setOpenModal(true);
            }
        }
        // Always fetch data regardless of whether state is passed
        fetchAllDeceased();
        fetchAllClients();
    }, [location.state, navigate]);

    // Fetch data on component mount
    useEffect(() => {
        fetchAllDeceased();
        fetchAllClients();
        fetchPayments();
    }, []);

    const fetchAllDeceased = async () => {
        try {
            const response = await axios.get(`${BASEURL}/deceased/`);
            setAllDeceased(response.data);
        } catch (error) {
            console.log('Error fetching deceased:', error);
        }
    };

    const fetchAllClients = async () => {
        try {
            const response = await axios.get(`${BASEURL}/clients/all_clients`);
            setAllClients(response.data || []);
        } catch (error) {
            console.log('Error fetching clients:', error);
        }
    };

    const fetchPayments = async () => {
        try {
            setLoading(true);
            // Fetch renewal payments for this specific deceased person
            const response = await axios.get(`${BASEURL}/renewal-payments/`);
            setPayments(response.data);
            
            toast.success('Payments loaded successfully!', {
                position: toast.POSITION.TOP_CENTER
            });
        } catch (error) {
            console.log('Error fetching payments:', error);
            toast.error('Failed to load payments', {
                position: toast.POSITION.TOP_CENTER
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchFeeData = async (saleOfGrave) => {
        try {
            const response = await axios.get(`${BASEURL}/fee/${1}/${saleOfGrave}`);
            const feeAmount = response.data;
            
            // Fetch maintenance fees to calculate percentage
            const maintenanceResponse = await axios.get(`${BASEURL}/maintenance-fees`);
            const maintenanceFees = maintenanceResponse.data;
            
            if (maintenanceFees.length > 0 && feeAmount) {
                const maintenanceFeePercentage = maintenanceFees[0].maintainance_fees || 0;
                const calculatedAmount = (parseFloat(feeAmount) * parseFloat(maintenanceFeePercentage)) / 100;
                setMaintenanceFeeAmount(calculatedAmount);
                
                // Update form data with calculated amount
                setFormData(prev => ({
                    ...prev,
                    renewal_amount: calculatedAmount.toString(),
                    balance: '0'
                }));
            }
        } catch (error) {
            console.log('Error fetching fee data:', error);
        }
    };

    const handleBack = () => {
        navigate('/renew', {
            state: {
                id: deceasedId
            }
        });
    };

    const handleAddPayment = () => {
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setFormData({
            customer_name: '',
            renewal_amount: '',
            balance: '',
            deceasedid: deceasedId
        });
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => {
            const newData = {
                ...prev,
                [field]: value
            };
            
            // Auto-calculate balance when renewal_amount changes
            if (field === 'renewal_amount' && value) {
                const renewalAmount = parseFloat(value) || 0;
                const calculatedBalance = maintenanceFeeAmount - renewalAmount;
                newData.balance = calculatedBalance > 0 ? calculatedBalance.toString() : '0';
            }
            
            return newData;
        });
    };

    const handleSubmitPayment = async () => {
        if (!formData.customer_name || !formData.renewal_amount || !formData.balance || !formData.deceasedid) {
            toast.error('Please fill in all fields', {
                position: toast.POSITION.TOP_CENTER
            });
            return;
        }

        try {
            setSubmitting(true);
            const response = await axios.post(`${BASEURL}/renewal-payments/`, {
                customer_name: formData.customer_name,
                renewal_amount: parseFloat(formData.renewal_amount),
                balance: parseFloat(formData.balance),
                deceasedid: formData.deceasedid
            });

            if (response.data) {
                toast.success('Payment added successfully!', {
                    position: toast.POSITION.TOP_CENTER
                });
                handleCloseModal();
                fetchPayments(); // Refresh the list
            }
        } catch (error) {
            console.log('Error adding payment:', error);
            toast.error('Failed to add payment', {
                position: toast.POSITION.TOP_CENTER
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleViewPayment = (paymentId) => {
        navigate('/view-renew-payment', {
            state: {
                paymentId: paymentId,
                deceasedId: deceasedId,
                deceasedData: deceasedData
            }
        });
    };

    const handleEditPayment = (payment) => {
        console.log('Payment object for editing:', payment);
        setSelectedPayment(payment);
        setEditFormData({
            customer_name: payment.customer_name || '',
            renewal_amount: payment.renewal_amount || '',
            balance: payment.balance || '',
            deceasedid: payment.deceasedid || deceasedId
        });
        setOpenEditModal(true);
    };

    const handleDeletePayment = (payment) => {
        setSelectedPayment(payment);
        setOpenDeleteModal(true);
    };

    const handleCloseEditModal = () => {
        setOpenEditModal(false);
        setSelectedPayment(null);
        setEditFormData({
            customer_name: '',
            renewal_amount: '',
            balance: '',
            deceasedid: deceasedId
        });
    };

    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
        setSelectedPayment(null);
    };

    const handleEditInputChange = (field, value) => {
        setEditFormData(prev => {
            const newData = {
                ...prev,
                [field]: value
            };
            
            // Auto-calculate balance when renewal_amount changes
            if (field === 'renewal_amount' && value) {
                const renewalAmount = parseFloat(value) || 0;
                const calculatedBalance = maintenanceFeeAmount - renewalAmount;
                newData.balance = calculatedBalance > 0 ? calculatedBalance.toString() : '0';
            }
            
            return newData;
        });
    };

    const handleUpdatePayment = async () => {
        if (!editFormData.customer_name || !editFormData.renewal_amount || !editFormData.balance || !editFormData.deceasedid) {
            toast.error('Please fill in all fields', {
                position: toast.POSITION.TOP_CENTER
            });
            return;
        }

        try {
            setEditing(true);
            console.log('Updating payment with ID:', selectedPayment.tblid);
            console.log('Selected payment object:', selectedPayment);
            const response = await axios.put(`${BASEURL}/renewal-payments/${selectedPayment.tblid}`, {
                customer_name: editFormData.customer_name,
                renewal_amount: parseFloat(editFormData.renewal_amount),
                balance: parseFloat(editFormData.balance),
                deceasedid: editFormData.deceasedid
            });

            if (response.data) {
                toast.success('Payment updated successfully!', {
                    position: toast.POSITION.TOP_CENTER
                });
                handleCloseEditModal();
                fetchPayments(); // Refresh the list
            }
        } catch (error) {
            console.log('Error updating payment:', error);
            toast.error('Failed to update payment', {
                position: toast.POSITION.TOP_CENTER
            });
        } finally {
            setEditing(false);
        }
    };

    const handleConfirmDelete = async () => {
        try {
            setDeleting(true);
            await axios.delete(`${BASEURL}/renewal-payments/${selectedPayment.tblid}`);
            toast.success('Payment deleted successfully!', {
                position: toast.POSITION.TOP_CENTER
            });
            handleCloseDeleteModal();
            fetchPayments(); // Refresh the list
        } catch (error) {
            console.log('Error deleting payment:', error);
            toast.error('Failed to delete payment', {
                position: toast.POSITION.TOP_CENTER
            });
        } finally {
            setDeleting(false);
        }
    };

    const handleExportToExcel = () => {
        // Create CSV content
        const headers = ['ID', 'Customer Name', 'Renewal Amount', 'Balance', 'Deceased Name', 'Created Date'];
        const csvContent = [
            headers.join(','),
            ...filteredPayments.map(payment => [
                payment.tblid,
                `"${payment.customer_name}"`,
                payment.renewal_amount,
                payment.balance,
                `"${payment.deceasedName || 'N/A'}"`,
                new Date(payment.createdAt).toLocaleDateString()
            ].join(','))
        ].join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `renewal_payments_export_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('Renewal payments exported successfully!', {
            position: toast.POSITION.TOP_CENTER
        });
    };

    const filteredPayments = payments.filter(payment => {
        const deceasedPerson = allDeceased.find(d => d.tblid === payment.deceasedid);
        const deceasedName = deceasedPerson ? 
            `${deceasedPerson.fname} ${deceasedPerson.mname} ${deceasedPerson.lname}` : 
            '';
        
        return payment.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               payment.renewal_amount?.toString().includes(searchTerm) ||
               payment.balance?.toString().includes(searchTerm) ||
               deceasedName.toLowerCase().includes(searchTerm.toLowerCase());
    });

    return (
        <>
            <ToastContainer />
            <div className='grid grid-cols-12'>
                <div className='col-span-2 hidden lg:block'>
                    <SideBar />
                </div>
                <div className='col-span-12 lg:col-span-10 bg-[#D2D2CF]'>
                    <Header />
                    <div className='mx-8'>
                        <div className="flex items-center justify-between py-6">
                            <div>
                                <h1 className="text-4xl font-bold text-[#111927] py-2">
                                  Renewal Payment List
                                </h1>
                                {deceasedData && (
                                    <Typography variant="h6" className="text-gray-600">
                                        For: {deceasedData.fname} {deceasedData.mname} {deceasedData.lname}
                                    </Typography>
                                )}
                            </div>
                            <div className="flex space-x-2">
                                {/* <Button 
                                    onClick={handleBack}
                                    variant="outlined" 
                                    style={{ borderColor: '#0F1D48', color: '#0F1D48' }}
                                >
                                    Back to Renew
                                </Button> */}
                                <Button 
                                    onClick={handleExportToExcel} 
                                    variant="outlined" 
                                    startIcon={<FileDownload />}
                                    style={{ borderColor: '#0F1D48', color: '#0F1D48' }}
                                >
                                    Export to Excel
                                </Button>
                                <Button 
                                    onClick={handleAddPayment}
                                    variant="contained" 
                                    style={{ backgroundColor: '#22C55E', color: 'white' }}
                                >
                                    <Add className="mr-2" />
                                    Add Payment
                                </Button>
                            </div>
                        </div>

                        {/* Fee Information Display */}
                        {/* {maintenanceFeeAmount > 0 && (
                            <Paper elevation={3} className="bg-gradient-to-r from-green-50 to-blue-50 mt-8 p-6 mb-6" style={{ borderRadius: 20, border: '2px solid #22C55E' }}>
                                <Typography variant="h6" className="font-bold text-green-800 mb-4">
                                    💰 Payment Information
                                </Typography>
                                <div className="text-center p-4 bg-white rounded-lg">
                                    <Typography variant="subtitle2" color="textSecondary" className="mb-1">
                                        Amount to Pay
                                    </Typography>
                                    <Typography variant="h4" className="font-bold text-green-600">
                                        GH₵ {Number(maintenanceFeeAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </div>
                            </Paper>
                        )} */}

                        <Paper elevation={3} className="bg-white mt-8 p-6 mb-8" style={{ borderRadius: 20 }}>
                            {/* Search Bar */}
                            <div className="mb-6">
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Search payments by customer name, amount, or payment method..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>

                            {/* Payments Table */}
                            {loading ? (
                                <div className="flex justify-center items-center py-12">
                                    <CircularProgress size={50} style={{ color: "#122E76" }} />
                                    <Typography className="ml-4">Loading payments...</Typography>
                                </div>
                            ) : (
                                <TableContainer>
                                    <Table>
                                        <TableHead style={{ backgroundColor: "#F8F9FA" }}>
                                            <TableRow>
                                                <TableCell style={{ fontWeight: "bold", fontSize: "16px", fontFamily: "'Poppins', sans-serif", paddingLeft: 21 }}>
                                                    Customer Name
                                                </TableCell>
                                                <TableCell style={{ fontWeight: "bold", fontSize: "16px", fontFamily: "'Poppins', sans-serif" }}>
                                                    Renewal Amount
                                                </TableCell>
                                                <TableCell style={{ fontWeight: "bold", fontSize: "16px", fontFamily: "'Poppins', sans-serif" }}>
                                                    Balance
                                                </TableCell>
                                                <TableCell style={{ fontWeight: "bold", fontSize: "16px", fontFamily: "'Poppins', sans-serif" }}>
                                                    Deceased Person
                                                </TableCell>
                                                <TableCell style={{ fontWeight: "bold", fontSize: "16px", fontFamily: "'Poppins', sans-serif" }}>
                                                    Created Date
                                                </TableCell>
                                                <TableCell style={{ fontWeight: "bold", fontSize: "16px", fontFamily: "'Poppins', sans-serif" }}>
                                                    Actions
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredPayments.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                                                        {payments.length === 0 ? 'No payments found for this deceased person.' : 'No payments found matching your search.'}
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                filteredPayments.map((payment, index) => {
                                                    // Find deceased person data
                                                    const deceasedPerson = allDeceased.find(d => d.tblid === payment.deceasedid);
                                                    const deceasedName = deceasedPerson ? 
                                                        `${deceasedPerson.fname} ${deceasedPerson.mname} ${deceasedPerson.lname}` : 
                                                        'Unknown';
                                                    
                                                    return (
                                                        <TableRow key={payment.tblid || index} hover>
                                                            <TableCell style={{ fontFamily: "'Poppins', sans-serif", paddingLeft: 21, fontWeight: "bold" }}>
                                                                {payment.customer_name || 'N/A'}
                                                            </TableCell>
                                                            <TableCell style={{ fontFamily: "'Poppins', sans-serif", fontWeight: "bold", color: "#22C55E" }}>
                                                                GH₵ {Number(payment.renewal_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </TableCell>
                                                            <TableCell style={{ fontFamily: "'Poppins', sans-serif", fontWeight: "bold", color: "#DC2626" }}>
                                                                GH₵ {Number(payment.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </TableCell>
                                                            <TableCell style={{ fontFamily: "'Poppins', sans-serif" }}>
                                                                {deceasedName}
                                                            </TableCell>
                                                            <TableCell style={{ fontFamily: "'Poppins', sans-serif" }}>
                                                                {payment.createdAt ? dayjs(payment.createdAt).format('MMM DD, YYYY') : 'N/A'}
                                                            </TableCell>
                                                            <TableCell style={{ fontFamily: "'Poppins', sans-serif" }}>
                                                                <div className="flex space-x-1">
                                                                    {/* <Tooltip title="View Payment">
                                                                        <IconButton 
                                                                            onClick={() => handleViewPayment(payment.id)}
                                                                            size="small"
                                                                            style={{ color: '#0F1D48' }}
                                                                        >
                                                                            <Visibility />
                                                                        </IconButton>
                                                                    </Tooltip> */}
                                                                    <Tooltip title="Edit Payment">
                                                                        <IconButton 
                                                                            onClick={() => handleEditPayment(payment)}
                                                                            size="small"
                                                                            style={{ color: '#22C55E' }}
                                                                        >
                                                                            <Edit />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Delete Payment">
                                                                        <IconButton 
                                                                            onClick={() => handleDeletePayment(payment)}
                                                                            size="small"
                                                                            style={{ color: '#DC2626' }}
                                                                        >
                                                                            <Delete />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>
                    </div>
                </div>
            </div>

            {/* Add Payment Modal */}
            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Typography variant="h5" className="font-bold text-[#0F1D48]">
                        Add Renewal Payment
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <div className="space-y-4 mt-4">
                        <Autocomplete
                            options={allClients}
                            getOptionLabel={(option) => {
                                if (option.fname && option.lname) {
                                    return `${option.fname} ${option.lname}`.trim();
                                }
                                return option.username || option.name || `Client ${option.tblid || option.id}`;
                            }}
                            value={allClients.find(c => {
                                const clientName = c.fname && c.lname ? `${c.fname} ${c.lname}`.trim() : c.username || c.name;
                                return clientName === formData.customer_name;
                            }) || null}
                            onChange={(event, newValue) => {
                                const clientName = newValue ? 
                                    (newValue.fname && newValue.lname ? `${newValue.fname} ${newValue.lname}`.trim() : newValue.username || newValue.name) : '';
                                handleInputChange('customer_name', clientName);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Customer"
                                    variant="outlined"
                                    placeholder="Search for customer..."
                                    required
                                />
                            )}
                            renderOption={(props, option) => {
                                const displayName = option.fname && option.lname 
                                    ? `${option.fname} ${option.lname}`.trim()
                                    : option.username || option.name || `Client ${option.tblid || option.id}`;
                                return (
                                    <li {...props} key={option.tblid || option.id}>
                                        {displayName}
                                    </li>
                                );
                            }}
                            isOptionEqualToValue={(option, value) => 
                                (option.tblid || option.id) === (value?.tblid || value?.id)
                            }
                        />
                        
                        <TextField
                            fullWidth
                            label="Renewal Amount"
                            variant="outlined"
                            type="number"
                            value={formData.renewal_amount}
                            onChange={(e) => handleInputChange('renewal_amount', e.target.value)}
                            required
                            helperText="Enter the amount to be paid (default: maintenance fee amount)"
                            InputProps={{
                                startAdornment: <InputAdornment position="start">GH₵</InputAdornment>,
                            }}
                        />
                        
                        <TextField
                            fullWidth
                            label="Balance"
                            variant="outlined"
                            type="number"
                            value={formData.balance}
                            onChange={(e) => handleInputChange('balance', e.target.value)}
                            required
                            helperText="Automatically calculated as: Amount to Pay - Renewal Amount"
                            InputProps={{
                                startAdornment: <InputAdornment position="start">GH₵</InputAdornment>,
                            }}
                        />
                        
                        {/* Calculation Summary */}
                        <Box className="p-4 bg-gray-50 rounded-lg">
                            <Typography variant="subtitle1" className="font-semibold mb-2">
                                💡 Calculation Summary
                            </Typography>
                            <div className="space-y-1">
                                <Typography variant="body2">
                                    <strong>Amount to Pay:</strong> GH₵ {Number(maintenanceFeeAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Renewal Amount:</strong> GH₵ {Number(formData.renewal_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                                <Typography variant="body2" className="font-semibold text-red-600">
                                    <strong>Balance:</strong> GH₵ {Number(formData.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                            </div>
                        </Box>
                        
                        <Autocomplete
                            options={allDeceased}
                            getOptionLabel={(option) => `${option.fname} ${option.mname} ${option.lname}`}
                            value={allDeceased.find(d => d.tblid === formData.deceasedid) || null}
                            onChange={(event, newValue) => {
                                handleInputChange('deceasedid', newValue ? newValue.tblid : deceasedId);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Deceased Person"
                                    variant="outlined"
                                    placeholder="Search by first name, middle name, or last name..."
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <div>
                                        <Typography variant="body1" className="font-semibold">
                                            {option.fname} {option.mname} {option.lname}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            ID: {option.tblid} | Gender: {option.gender} | Age: {option.Aged}
                                        </Typography>
                                    </div>
                                </Box>
                            )}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="primary">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSubmitPayment} 
                        variant="contained" 
                        style={{ backgroundColor: '#22C55E', color: 'white' }}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <CircularProgress size={20} style={{ color: 'white', marginRight: 8 }} />
                                Adding...
                            </>
                        ) : (
                            'Add Payment'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Payment Modal */}
            <Dialog open={openEditModal} onClose={handleCloseEditModal} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Typography variant="h5" className="font-bold text-[#0F1D48]">
                        Edit Renewal Payment
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <div className="space-y-4 mt-4">
                        <Autocomplete
                            options={allClients}
                            getOptionLabel={(option) => {
                                if (option.fname && option.lname) {
                                    return `${option.fname} ${option.lname}`.trim();
                                }
                                return option.username || option.name || `Client ${option.tblid || option.id}`;
                            }}
                            value={allClients.find(c => {
                                const clientName = c.fname && c.lname ? `${c.fname} ${c.lname}`.trim() : c.username || c.name;
                                return clientName === editFormData.customer_name;
                            }) || null}
                            onChange={(event, newValue) => {
                                const clientName = newValue ? 
                                    (newValue.fname && newValue.lname ? `${newValue.fname} ${newValue.lname}`.trim() : newValue.username || newValue.name) : '';
                                handleEditInputChange('customer_name', clientName);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Customer"
                                    variant="outlined"
                                    placeholder="Search for customer..."
                                    required
                                />
                            )}
                            renderOption={(props, option) => {
                                const displayName = option.fname && option.lname 
                                    ? `${option.fname} ${option.lname}`.trim()
                                    : option.username || option.name || `Client ${option.tblid || option.id}`;
                                return (
                                    <li {...props} key={option.tblid || option.id}>
                                        {displayName}
                                    </li>
                                );
                            }}
                            isOptionEqualToValue={(option, value) => 
                                (option.tblid || option.id) === (value?.tblid || value?.id)
                            }
                        />
                        
                        <TextField
                            fullWidth
                            label="Renewal Amount"
                            variant="outlined"
                            type="number"
                            value={editFormData.renewal_amount}
                            onChange={(e) => handleEditInputChange('renewal_amount', e.target.value)}
                            required
                            helperText="Enter the amount to be paid (default: maintenance fee amount)"
                            InputProps={{
                                startAdornment: <InputAdornment position="start">GH₵</InputAdornment>,
                            }}
                        />
                        
                        <TextField
                            fullWidth
                            label="Balance"
                            variant="outlined"
                            type="number"
                            value={editFormData.balance}
                            onChange={(e) => handleEditInputChange('balance', e.target.value)}
                            required
                            helperText="Automatically calculated as: Amount to Pay - Renewal Amount"
                            InputProps={{
                                startAdornment: <InputAdornment position="start">GH₵</InputAdornment>,
                            }}
                        />
                        
                        {/* Calculation Summary */}
                        <Box className="p-4 bg-gray-50 rounded-lg">
                            <Typography variant="subtitle1" className="font-semibold mb-2">
                                💡 Calculation Summary
                            </Typography>
                            <div className="space-y-1">
                                <Typography variant="body2">
                                    <strong>Amount to Pay:</strong> GH₵ {Number(maintenanceFeeAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Renewal Amount:</strong> GH₵ {Number(editFormData.renewal_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                                <Typography variant="body2" className="font-semibold text-red-600">
                                    <strong>Balance:</strong> GH₵ {Number(editFormData.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                            </div>
                        </Box>
                        
                        <Autocomplete
                            options={allDeceased}
                            getOptionLabel={(option) => `${option.fname} ${option.mname} ${option.lname}`}
                            value={allDeceased.find(d => d.tblid === editFormData.deceasedid) || null}
                            onChange={(event, newValue) => {
                                handleEditInputChange('deceasedid', newValue ? newValue.tblid : deceasedId);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Deceased Person"
                                    variant="outlined"
                                    placeholder="Search by first name, middle name, or last name..."
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <div>
                                        <Typography variant="body1" className="font-semibold">
                                            {option.fname} {option.mname} {option.lname}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            ID: {option.tblid} | Gender: {option.gender} | Age: {option.Aged}
                                        </Typography>
                                    </div>
                                </Box>
                            )}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditModal} color="primary">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleUpdatePayment} 
                        variant="contained" 
                        style={{ backgroundColor: '#22C55E', color: 'white' }}
                        disabled={editing}
                    >
                        {editing ? (
                            <>
                                <CircularProgress size={20} style={{ color: 'white', marginRight: 8 }} />
                                Updating...
                            </>
                        ) : (
                            'Update Payment'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={openDeleteModal} onClose={handleCloseDeleteModal} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h5" className="font-bold text-red-600">
                        🗑️ Delete Payment
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <div className="mt-4">
                        <Typography variant="body1" className="mb-4">
                            Are you sure you want to delete this payment?
                        </Typography>
                        {selectedPayment && (
                            <Box className="p-4 bg-red-50 rounded-lg border border-red-200">
                                <Typography variant="subtitle1" className="font-semibold text-red-800 mb-2">
                                    Payment Details:
                                </Typography>
                                <div className="space-y-1">
                                    <Typography variant="body2">
                                        <strong>Customer:</strong> {selectedPayment.customer_name}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Renewal Amount:</strong> GH₵ {Number(selectedPayment.renewal_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Balance:</strong> GH₵ {Number(selectedPayment.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </div>
                            </Box>
                        )}
                        <Typography variant="body2" className="text-red-600 mt-4">
                            ⚠️ This action cannot be undone.
                        </Typography>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteModal} color="primary">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmDelete} 
                        variant="contained" 
                        style={{ backgroundColor: '#DC2626', color: 'white' }}
                        disabled={deleting}
                    >
                        {deleting ? (
                            <>
                                <CircularProgress size={20} style={{ color: 'white', marginRight: 8 }} />
                                Deleting...
                            </>
                        ) : (
                            'Delete Payment'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default RenewPaymentList;
